<template>
    <div>
        <!-- filter -->
        <v-row class="my-2">
            <!-- city region filter -->
            <v-col lg="4" md="5" sm="8" cols="12" class="pa-1">
                <v-row class="justify-space-between ma-0">
                    <!-- city name -->
                    <v-col
                        md="5"
                        sm="4"
                        cols="6"
                        class="pa-0"
                        :class="{ 
                            'pe-1': $i18n.locale === 'en',
                            'ps-1': $i18n.locale === 'ar'
                        }"
                    >
                        <v-autocomplete
                            v-model="cityId"
                            :items="cities"
                            item-text="name"
                            item-value="id"
                            :label="$t('inputs.city')"
                            dense
                            prepend-icon="mdi-map-marker-multiple"
                            hide-details
                            append-icon
                            multiple
                            clearable
                            :disabled="loading"
                            :search-input.sync="searchCityId"
                            @change="setRegions(); searchCityId = null"
                            outlined
                        >
                            <template v-slot:prepend-item>
                                <v-list-item
                                    ripple
                                    @click="toggleCities"
                                >
                                    <v-list-item-action>
                                        <v-icon>
                                            {{ iconCities }}
                                        </v-icon>
                                    </v-list-item-action>
                                    
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ $t('select-all') }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider class="mt-2"></v-divider>
                            </template>

                            <template v-slot:selection="{ item, index }">
                                <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                                <span
                                    v-if="index === 1"
                                    class="grey--text text-caption"
                                >
                                    (+{{ cityId.length - 1 }})
                                </span>
                            </template>
                        </v-autocomplete>
                    </v-col>
        
                    <!-- region -->
                    <v-col md="7" sm="8" cols="6" class="pa-0">
                        <v-autocomplete
                            v-model="regionIds"
                            :items="regionsAll.filter(c => cityId.includes(c.cityId)).sort(function(a, b) { if(a.name < b.name) {return -1}; if(a.name > b.name) { return 1 }; return 0 })"
                            item-text="name"
                            item-value="id"
                            :label="$t('inputs.region')"
                            dense
                            multiple
                            clearable
                            hide-details
                            append-icon
                            :loading="regionsLoading"
                            :disabled="loading || regionsLoading || cityId === []"
                            outlined
                            :search-input.sync="searchRegion"
                            append-outer-icon="mdi-select-search"
                            @click:append-outer="getMedical()"
                        >
                            <!-- customerType !== null ? getEntityForType(customerType) : null -->
                            <template v-slot:selection="{ item, index }">
                                <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                                <span
                                    v-if="index === 1"
                                    class="grey--text text-caption"
                                >
                                    (+{{ regionIds.length - 1 }})
                                </span>
                            </template>

                            <template v-slot:prepend-item>
                                <v-list-item ripple @click="toggle">
                                    <v-list-item-action>
                                        <v-icon>
                                            {{ icon }}
                                        </v-icon>
                                    </v-list-item-action>
                                    
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ $t('select-all') }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider class="mt-2"></v-divider>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
            </v-col>
            
            <v-divider vertical class="my-3 mx-1 grey"/>

            <!-- date -->
            <v-col cols="auto" class="pa-0">
                <v-radio-group
                    v-model.number="dateType"
                    row
                    class="mt-1"
                    hide-details
                    :disabled="loading"
                >
                    <v-radio
                        :label="$t('daily-date')"
                        :value="1"
                    ></v-radio>
                    <v-radio
                        :label="$t('from-date') + ' / ' + $t('to-date')"
                        :value="2"
                    ></v-radio>
                </v-radio-group>
            </v-col>

            <v-col v-if="dateType === 2" cols="6" sm="3" md="2" class="pa-1">
                <!-- start date -->
                <bee-date-picker
                    v-model="startDate"
                    :input-label="$t('inputs.start-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :allowed-dates="disableDates"
                    :disabled="loading"
                ></bee-date-picker>
            </v-col>

            <v-col v-if="dateType === 2" cols="6" sm="3" md="2" class="pa-1">
                <!-- end date -->
                <bee-date-picker
                    v-model="endDate"
                    :input-label="$t('inputs.end-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :allowed-dates="disableDates"
                    :disabled="loading"
                ></bee-date-picker>
            </v-col>
     
            <v-col v-if="dateType === 1" cols="12" sm="6" md="3" class="pa-1">
                <!-- date -->
                <bee-date-input
                    v-model="date"
                    :append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left' "
                    :prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right' "
                    :label="$t('inputs.date')"
                    input-format-order="DD-MM-YYYY"
                    dense
                    :loading="loading"
                    :disabled="loading"
                    hide-details
                    @click:append="
                        addDate();
                        filter()
                    "
                    @click:prepend="
                        subDate();
                        filter()
                    "
                ></bee-date-input>
            </v-col>
                        
            <v-divider vertical class="my-3 mx-1 grey"/>
            <v-spacer />
            <!-- medical rep name -->
            <v-col md="3" sm="3" cols="12" class="pa-1 d-flex align-start">
                <v-autocomplete
                    v-model="medicalRepIds"
                    :items="regionIds.length === 0 ? medicalRepsBrief.filter(c => c.employeeType === userType.medicalRep) : medicalRepsForRegion.filter(c => c.employeeType === userType.medicalRep)"
                    item-text="name"
                    item-value="id"
                    :label="$t('inputs.medical-rep')"
                    prepend-icon="mdi-account-tie"
                    hide-details
                    clearable
                    :loading="loading || loadingMedicalForRegion"
                    :disabled="loading"
                    outlined
                    dense
                    multiple
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        >
                            (+{{ medicalRepIds.length - 1 }})
                        </span>
                    </template>
                </v-autocomplete>
            </v-col>

            <!-- team -->
            <v-col cols="2" md="2" sm="3" class="pa-1 d-flex align-start">
                <v-autocomplete
                    v-model="teamId"
                    :items="teams"
                    item-text="name"
                    item-value="id"
                    dense
                    hide-details
                    outlined
                    :label="$t('inputs.team')"
                    :return-object="false"
                    :search-input.sync="searchTeam"
                    @change="searchTeam = null"
                    :disabled="loading"
                    prepend-icon="mdi-microsoft-teams"
                />
            </v-col>

            <v-col cols="auto" class="pa-1">
                <v-btn
                    color="success"
                    class="px-sm-4 mx-2"
                    :loading="loading"
                    :disabled="loading || isAllFieldsEmpty"
                    small
                    @click="fetch()"
                >
                    <v-icon class="me-1"> mdi-magnify </v-icon>
                    {{$t('search')}}
                </v-btn>
        
                <v-btn
                    class="px-sm-8"
                    @click="needToRestShownItems = true; clearFilter(); filter()"
                    :disabled="(loading) && !$route.query.visitId"
                    color="alert-color white--text"
                    small
                >
                    {{ $t('reset') }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- table -->
        <bee-handy-table
            :headers="headers"
            :items="items"
            :items-per-page="-1"
            hide-default-footer
            dense
            zebra
            height="450"
        ></bee-handy-table>
    </div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import { disableDates } from '@/helpers/functions'
import { enableVacations } from '@/configs/global.config'
import { userType } from '@/helpers/enums'
export default {
    name: 'comprehensive-report',

    data() {
        return {
            // loadings
            loading: false,
            loadingMedicalForRegion: false,
            regionsLoading: false,

            // filter
            regionIds: [],
            cityId: [],
            searchCityId: null,
            searchRegion: null,
            searchTeam: null,
            dateType: 1,
            medicalRepIds: [],
            medicalRepsForRegion: [],
            teamId: null,

            startDate: this.moment().startOf('month').format('YYYY-MM-DD'),
            endDate: this.moment().endOf('month').format('YYYY-MM-DD'),
            date: this.moment().format('YYYY-MM-DD'),

            // for table
            items: [],

            // function from helper
            disableDates,

            // enums
            userType
        }
    },

    computed: {
        ...mapState({
            cities: state => state.cities.cities,
            regionsAll: state => state.regions.regionsAll,
            vacationsDayDetails: state => state.vacations.vacationsDayDetails,
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            teams: state => state.teams.teams,
            customersTypes: state => state.entityType.customersTypes
        }),

        ...mapGetters({
            getTeamById: 'teams/getTeamById',
        }),

        iconCities () {
            return this.cityId.length === this.cities.length ? 'mdi-close-box' : 'mdi-checkbox-blank-outline'
        },

        icon () {
            return this.regionIds.length === this.regionsAll.length ? 'mdi-close-box' : 'mdi-checkbox-blank-outline'
        },

        isAllFieldsEmpty(){
            return (
                !this.medicalRepIds && this.regionIds.length === 0 && !this.teamId && !this.startDate && !this.endDate
            )
        },

        headers () {
            return [
                {
                    text: this.$t('headers.employee'),
                    name: 'employeeId',
                    value: 'employeeId',
                    setValueExpr: val => this.medicalRepsBrief.find(c => c.id === val)?.name,
                    sortable: false
                },
                ...this.customersTypes.map(c => ({
                    text: this.$t('headers.visit-count') + ' ' + c.name,
                    name: c.name,
                    value: 'visitsByEntityType',
                    setValueExpr: val => val?.find(f => f.entityTypeId === c.id)?.visitCount || 0
                })),
                {
                    text: this.$t('headers.actual-working-days'),
                    name: 'visitCount',
                    value: 'visitCount',
                },
                {
                    text: this.$t('headers.annual-leave-count'),
                    name: 'annualLeaveCount',
                    value: 'annualLeaveCount',
                },
                {
                    text: this.$t('headers.sick-leave-count'),
                    name: 'sickLeaveCount',
                    value: 'sickLeaveCount'
                },
                {
                    text: this.$t('headers.daily-average'),
                    name: 'dailyAverage',
                    value: 'dailyAverage'
                }
            ]  
        }

        // headersTotal () {
        //     retu
        // }
    },

    methods: {
        moment,

        fetch () {
            this.loading = true
            this.$store.dispatch('visits/fetchEmployeeVisitStatistics', { 
                teamId: this.teamId,
                employeeIds: this.medicalRepIds,
                fromDate: this.dateType === 1 ? this.date : this.startDate, 
                toDate: this.dateType === 1 ? this.date : this.endDate,
                regionIds: this.regionIds,
            }).then((data) => {
                console.log('data', data)
                this.items = data || []
            }).finally(() => {
                this.loading = false
            })
        },

        setRegions () {
            this.regionIds = [];
            if (!this.cityId) return;
            this.regionsAll.forEach(region => {
                if (this.cityId?.includes(region.cityId)) {
                    this.regionIds.push(region?.id);
                }
            });
            if (this.regionIds.length) {
                this.loadingMedicalForRegion = true
                this.$store.dispatch('medicalreps/fetchAllBrief', { regionIds: this.regionIds }).then((data) => {
                    this.medicalRepsForRegion = data
                }).finally(() => {
                    this.loadingMedicalForRegion = false
                })
            }
        },

        toggleCities () {
            this.$nextTick(() => {
                if (this.cityId.length === this.cities.length) {
                    this.cityId = []
                    this.regionIds = []
                } 
                else {
                    this.cityId = this.cities.map(c => c.id);
                    this.setRegions()
                }
            })
        },

        toggle () {
            this.$nextTick(() => {
                if (this.regionIds.length === this.regionsAll.length) {
                    this.regionIds = []
                } 
                else {
                    this.regionIds = this.regionsAll.map(c => c.id);
                }
            })
        },

        getMedical() {
            if (this.regionIds.length) {
                this.loadingMedicalForRegion = true
                this.medicalRepIds = []
                this.$store.dispatch('medicalreps/fetchAllBrief', { regionIds: this.regionIds }).then((data) => {
                    this.medicalRepsForRegion = data
                }).finally(() => {
                    this.loadingMedicalForRegion = false
                })
            }
        },

        addDate () {
            this.date = moment(this.date).add(1, 'day').format('YYYY-MM-DD');
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) ||
                (this.enableVacations && this.vacationsDayDetails?.includes(this.date))
            ) {
                this.addDate()
            }
            return this.date
        },

        subDate() {
            this.date = moment(this.date).subtract(1, 'day').format('YYYY-MM-DD')
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails?.includes(this.date))
            ) {
                this.subDate()
            }
            return this.date
        },

        clearFilter(){
            // clear fields
            this.medicalRepIds = [];
            this.cityId = [];
            this.regionIds = [];
            this.entityId = [];
            this.startDate = this.moment().format('YYYY-MM-DD');
            this.endDate = this.moment().format('YYYY-MM-DD');
            this.customerType = null;
            this.specialtyId = null;
            this.workload = null
            this.visit = null;
            this.$router.replace({
                query: {}
            })
            this.items = []
            // reset total visites
            this.totalVisits = null;

            this.isFiltered = false
        },
    },

    created () {
        this.loading = true
        Promise.all([
            !this.cities.length ? this.$store.dispatch('cities/fetchCityMine', {}) : null,
            !this.regionsAll.length ? this.$store.dispatch('regions/fetchAllRegions', {}) : null,

            enableVacations && !this.vacationsDayDetails.length ? this.$requestStack.setRequest({
                key: 'vacations/fetchAll',
                promise: this.$store.dispatch('vacations/fetchAll'),
            }) : this.$requestStack.getRequest('vacations/fetchAll').promise,

            !this.medicalRepsBrief.length ? this.$requestStack.setRequest({
                key: 'medicalreps/fetchAllBrief',
                promise: this.$store.dispatch('medicalreps/fetchAllBrief', true)
            }) : this.$requestStack.getRequest('medicalreps/fetchAllBrief').promise,

            !this.customersTypes.length ? this.$store.dispatch('entityType/fetchAll') : null,
            !this.teams.length ? this.$store.dispatch('teams/fetchAll') : null,
        ]).finally(() => {
            this.loading = false
        })
    }
}
</script>
